import { Form, Link, NavLink, useParams, useSubmit } from '@remix-run/react'
import { Trophy, Home, Building2, Calendar, Medal, BarChart3, LogOut, Settings, Users } from 'lucide-react'
import React, { useState } from 'react'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import { getBestRole, cn } from '#app/utils/misc.tsx'

const General = ({ user }: { user: any }) => {
	const { competitionId } = useParams<{ competitionId: string }>()
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const submit = useSubmit()

	const bestRole = getBestRole(user?.roles || [])

	const navItems = competitionId ? [
		{ to: `/competitions/${competitionId}/overview`, label: 'Overview', icon: <Home className="h-4 w-4" /> },
		{ to: `/competitions/${competitionId}/clubs`, label: 'Clubs', icon: <Building2 className="h-4 w-4" /> },
		{ to: `/competitions/${competitionId}/matches`, label: 'Fixtures', icon: <Calendar className="h-4 w-4" /> },
		{ to: `/competitions/${competitionId}/standings`, label: 'Standings', icon: <Medal className="h-4 w-4" /> },
		{ to: `/competitions/${competitionId}/stats`, label: 'Stats', icon: <BarChart3 className="h-4 w-4" /> },
	] : [
		{ to: '/competitions', label: 'Competitions', icon: <Trophy className="h-4 w-4" /> },
	]

	return (
		<nav className="flex h-full items-center space-x-3">
			{/* Desktop Navigation */}
			<div className="hidden lg:block">
				<div className="rounded-md bg-background-dark-foreground/60 p-0.5 backdrop-blur-sm">
					<ul className="flex items-center">
						{navItems.map((item) => (
							<li key={item.to}>
								<NavLink
									to={item.to}
									end={item.to === '/competitions'}
									className={({ isActive }) =>
										cn(
											'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200',
											{
												'bg-background-dark text-white/90': isActive,
												'text-white/70 hover:bg-background-dark/70 hover:text-white/90': !isActive,
											},
										)
									}
								>
									{item.icon}
									{item.label}
								</NavLink>
							</li>
						))}
					</ul>
				</div>
			</div>

			{/* User Menu */}
			<div className="flex items-center">
				{user ? (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button className="flex items-center gap-2 rounded-md bg-background-dark-foreground/60 px-3 py-1.5 transition-all duration-200 hover:bg-background-dark">
								{user.avatar ? (
									<img
										src={user.avatar}
										alt={user.name}
										className="h-7 w-7 rounded-full object-cover ring-1 ring-background-dark"
									/>
								) : (
									<div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-white ring-1 ring-background-dark">
										{user.name?.charAt(0).toUpperCase()}
									</div>
								)}
								<span className="text-sm font-medium text-white/90">{user.name}</span>
								<Icon name="chevron-down" className="size-4 text-white/60" />
							</button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-56 bg-background-dark-foreground border-background-dark">
							{bestRole === 'USER' && (
								<DropdownMenuItem asChild>
									<Link to="/settings/profile" className="flex items-center gap-2 text-white/80 hover:text-white">
										<Icon name="user" className="size-4 text-white/60" />
										<span>Edit Profile</span>
									</Link>
								</DropdownMenuItem>
							)}

							{bestRole === 'PLAYER' && (
								<>
									<DropdownMenuItem asChild>
										<Link to={`/player/${user?.playerProfile?.id}`} className="flex items-center gap-2 text-white/80 hover:text-white">
											<Icon name="user" className="size-4 text-white/60" />
											<span>View Profile</span>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link to={`/player/${user?.playerProfile?.id}/edit/personal-info`} className="flex items-center gap-2 text-white/80 hover:text-white">
											<Icon name="user" className="size-4 text-white/60" />
											<span>Edit Profile</span>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link to="/settings" className="flex items-center gap-2 text-white/80 hover:text-white">
											<Icon name="cog" className="size-4 text-white/60" />
											<span>Settings</span>
										</Link>
									</DropdownMenuItem>
								</>
							)}

							{bestRole === 'TEAM_OWNER' && (
								<>
									<DropdownMenuItem asChild>
										<Link to="/settings/profile" className="flex items-center gap-2 text-white/80 hover:text-white">
											<Icon name="user" className="size-4 text-white/60" />
											<span>Edit Profile</span>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link to={`/team/${user?.team[0]?.slug}/summary`} className="flex items-center gap-2 text-white/80 hover:text-white">
											<Icon name="users" className="size-4 text-white/60" />
											<span>View Team</span>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link to={`/team/${user?.team[0]?.slug}/settings`} className="flex items-center gap-2 text-white/80 hover:text-white">
											<Icon name="cog" className="size-4 text-white/60" />
											<span>Team Settings</span>
										</Link>
									</DropdownMenuItem>
								</>
							)}

							{bestRole === 'ORGANIZATION_OWNER' && (
								<>
									<DropdownMenuItem asChild>
										<Link to="/settings/profile" className="flex items-center gap-2 text-white/80 hover:text-white">
											<Icon name="user" className="size-4 text-white/60" />
											<span>Edit Profile</span>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link to={`/organisations/${user?.organization?.slug}/settings`} className="flex items-center gap-2 text-white/80 hover:text-white">
											<Icon name="cog" className="size-4 text-white/60" />
											<span>Organisation Settings</span>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link to={`/organisations/${user?.organization?.slug}/dashboard`} className="flex items-center gap-2 text-white/80 hover:text-white">
											<Icon name="grid" className="size-4 text-white/60" />
											<span>Dashboard</span>
										</Link>
									</DropdownMenuItem>
								</>
							)}

							{(bestRole === 'SUPER_ADMIN' || bestRole === 'SYSTEM_ADMIN') && (
								<>
									<DropdownMenuItem asChild>
										<Link to="/settings/profile" className="flex items-center gap-2 text-white/80 hover:text-white">
											<Icon name="user" className="size-4 text-white/60" />
											<span>Edit Profile</span>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link to={`/admin/dashboard`} className="flex items-center gap-2 text-white/80 hover:text-white">
											<Icon name="grid" className="size-4 text-white/60" />
											<span>Dashboard</span>
										</Link>
									</DropdownMenuItem>
								</>
							)}

							<DropdownMenuSeparator className="bg-background-dark/60" />
							<DropdownMenuItem
								className="text-red-400 focus:text-white focus:bg-red-600"
								onClick={(e) => {
									e.preventDefault()
									submit(e.currentTarget.closest('form'))
								}}
							>
								<Form
									className="flex w-full items-center gap-2"
									action="/logout"
									method="POST"
								>
									<LogOut className="size-4" />
									<span>Sign out</span>
								</Form>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				) : (
					<div className="flex items-center space-x-2">
						<Link
							to="/login"
							className="rounded-md bg-background-dark-foreground/60 px-3 py-1.5 text-sm font-medium text-white/80 transition-all duration-200 hover:bg-background-dark hover:text-white/90"
						>
							Sign in
						</Link>
						<Link
							to="/signup"
							className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white transition-all duration-200 hover:bg-primary/90"
						>
							Sign up
						</Link>
					</div>
				)}
			</div>

			{/* Mobile Menu Button */}
			<button
				className="rounded-md bg-background-dark-foreground/60 p-1.5 text-white/70 hover:text-white hover:bg-background-dark lg:hidden"
				onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
			>
				<Icon name={isMobileMenuOpen ? "close-heavy" : "menu"} className="size-4" />
			</button>

			{/* Mobile Menu */}
			{isMobileMenuOpen && (
				<div className="fixed inset-0 z-[100] lg:hidden">
					<div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
					<div className="absolute inset-y-0 right-0 w-full max-w-sm bg-background-dark shadow-xl">
						<div className="flex h-16 items-center justify-between border-b border-background-dark-foreground/30 px-4">
							<Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
								<img src="/img/logos/logo.png" alt="7even Sports" className="h-8" />
							</Link>
							<button
								onClick={() => setIsMobileMenuOpen(false)}
								className="rounded-md bg-background-dark-foreground/50 p-1.5 text-white/70 hover:text-white"
							>
								<Icon name="close-heavy" className="size-4" />
							</button>
						</div>

						<div className="h-[calc(100vh-4rem)] overflow-y-auto p-4">
							<ul className="space-y-1">
								{navItems.map((item) => (
									<li key={item.to}>
										<NavLink
											to={item.to}
											end={item.to === '/competitions'}
											onClick={() => setIsMobileMenuOpen(false)}
											className={({ isActive }) =>
												cn(
													'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200',
													{
														'bg-background-dark-foreground text-white/90': isActive,
														'text-white/70 hover:bg-background-dark-foreground/70 hover:text-white/90': !isActive,
													},
												)
											}
										>
											{item.icon}
											{item.label}
										</NavLink>
									</li>
								))}
							</ul>

							{user && (
								<div className="mt-4 border-t border-background-dark-foreground/30 pt-4">
									<div className="mb-3 flex items-center gap-3">
										{user.avatar ? (
											<img
												src={user.avatar}
												alt={user.name}
												className="h-8 w-8 rounded-full object-cover ring-1 ring-background-dark-foreground"
											/>
										) : (
											<div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white ring-1 ring-background-dark-foreground">
												{user.name?.charAt(0).toUpperCase()}
											</div>
										)}
										<div>
											<p className="font-medium text-white/90">{user.name}</p>
											<p className="text-xs text-white/60">{user.email}</p>
										</div>
									</div>

									<Form
										action="/logout"
										method="POST"
										className="w-full"
										onSubmit={() => setIsMobileMenuOpen(false)}
									>
										<button
											type="submit"
											className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-background-dark-foreground/50 hover:text-red-300"
										>
											<LogOut className="size-4" />
											Sign out
										</button>
									</Form>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</nav>
	)
}

export default General
